import {
  Code,
  Collapse,
  Flex,
  HStack,
  IconButton,
  InputGroup,
  InputRightElement,
  Kbd,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tooltip,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { ComponentProps, FC, useMemo } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { FiInfo, FiRefreshCw, FiSearch, FiTool } from 'react-icons/fi';
import ReactSelect from 'react-select';
import { defaultStyle as defaultAutocompleteInputStyle, FIXFieldsAutocompleteInput } from '../../../fix-session-manager/components/Autocomplete/FIXFieldsAutocompleteInput';
import { CodeBlock, CollapsableCodeBlock } from '../../../fix-session-manager/components/common/CodeBlock';
import { customStyles } from '../../../fix-session-manager/components/common/Select';
import { deepCopyAndApply } from '../utils';
import React from 'react';

const delimeter = ':'

const autocompleteInputStyle = deepCopyAndApply(defaultAutocompleteInputStyle, 
    style => {
        style['&multiLine'].highlighter.paddingRight = '64px'
        style['&multiLine'].input.paddingRight = '64px'
})

const composeRequest = (filteredSessions: any[], filter: string) => {
    const filteredSessionsQuery =
        filteredSessions.map((sessionID) => {
            const [sender, target] = sessionID.split(delimeter)
            return `(@SenderCompID == "${sender}" and @TargetCompID == "${target}" and @rw == "W") or (@SenderCompID == "${target}" and @TargetCompID == "${sender}" and @rw == "R")`
        })
        .join(' or ')

    let secondaryQuery = filter
    if (filter && filter.indexOf('@') < 0 && filter.indexOf('$')) {
        secondaryQuery = `@fullTextSearch == "${filter.replaceAll(/"/ig, '\\"')}"`
    }    

    return {
        filteredSessions: filteredSessions,
        primaryQuery: filteredSessionsQuery,
        secondaryQuery: secondaryQuery
    }
}

const TdName: FC<ComponentProps<typeof Td>> = ({ children }) => <Td width='120px' paddingInlineStart='0'>{children}</Td>
const TdValue: FC<ComponentProps<typeof Td>> = ({ children }) => <Td paddingInlineStart='0' paddingInlineEnd='0' >{children}</Td>

export const FIXMessagesSearchInput = memo((
    {
        fieldSuggestions,
        offeredSearchRequest,
        searchInProgress,
        sessions,
        handleSearch,
        dbQuery,
        searchResponse,
        showSessionIdSelector,
    }: {
        fieldSuggestions: any,
        offeredSearchRequest: any;
        searchInProgress: boolean;
        sessions: any[];
        handleSearch: (e: any) => void;
        dbQuery: string;
        searchResponse: any[];
        showSessionIdSelector: boolean;
    }) => {

    const [filteredSessions, setFilteredSessions] = useState<string[]>([])
    const [previousSessions, setPreviousSessions] = useState<any[]>([])
    
    const [query, setQuery] = useState<string>("")

    const [lastExecutedSearchRequest, setLastExecutedSearchRequest] = useState<any>(undefined)
    
    const searchRequest = composeRequest(filteredSessions, query)
    const isSearchChanged = JSON.stringify(lastExecutedSearchRequest) !== JSON.stringify(searchRequest)

    useEffect(() => {
        if (JSON.stringify(sessions) !== JSON.stringify(previousSessions)) {
            setPreviousSessions(sessions)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions])

    const sessionsOptions = useMemo(
        () => previousSessions.map((sessionID) => ({ value: sessionID, label: sessionID }))
    , [previousSessions])

    const composeRequestAndSearch = useCallback((filteredSessions: any[], query: string) => {
        const finalQuery = composeRequest(filteredSessions, query)
        setLastExecutedSearchRequest(finalQuery)
        handleSearch(finalQuery)
    }, [handleSearch])

    const search = useCallback(() => {
        composeRequestAndSearch(filteredSessions, query)
    }, [composeRequestAndSearch, query, filteredSessions])

    useEffect(() => {
        if (offeredSearchRequest) {
            const sessions = offeredSearchRequest.sessions ? offeredSearchRequest.sessions : filteredSessions;
            setFilteredSessions(sessions)
            
            const searchQuery = offeredSearchRequest.query !== undefined ? offeredSearchRequest.query : query;
            setQuery(searchQuery)
            
            composeRequestAndSearch(sessions, searchQuery)
        }
    }, [offeredSearchRequest, composeRequestAndSearch, filteredSessions, query])

    const handleKeyPress = useCallback((e) => {
        if (e.code === 'Enter' && e.shiftKey === false) {
            e.preventDefault()
            search()
        }
    }, [search])

    const { isOpen: isQueryDebugOpen, onToggle: toggleQueryDebug, onClose: closeQueryDebug } = useDisclosure()
    const { isOpen: isHelpOpen, onToggle: toggleHelp, onClose: closeHelp } = useDisclosure()

    const sessionIdSelector = () => (
        <ReactSelect
            placeholder='Session IDs'
            // @ts-ignore
            styles={{
                ...customStyles,
                container: (base) => ({
                    ...base,
                    width: '100%'
                })
            }}
            isClearable
            isMulti
            value={filteredSessions.map((sessionID) => ({ value: sessionID, label: sessionID }))}
            onChange={(values) => {
                setFilteredSessions(values.map((value) => value.value))
            }}
            options={sessionsOptions}
        />
    )

    const fixFieldsAutocompleteInput = (placeholder: string) => (
        <InputGroup marginTop={4}>
            <FIXFieldsAutocompleteInput
                // @ts-ignore
                fieldSuggestions={fieldSuggestions}
                value={query}
                onChange={setQuery}
                placeholder={placeholder}
                style={autocompleteInputStyle}
            />
            <InputRightElement boxSize='initial' margin={2}>
                <Tooltip
                    openDelay={500}
                    label='Toggle search debug information'
                >
                    <IconButton
                        onClick={() => {
                            toggleQueryDebug()
                            closeHelp()
                        }}
                        tabIndex={-1}
                        aria-label='Toggle search debug information'
                        icon={<FiTool />}
                        size='xs'
                        variant='ghost'
                        position='absolute'
                        right={-9}
                        color={isQueryDebugOpen ? 'initial' : 'transparent'}
                        _hover={{
                            color: 'initial'
                        }}
                    />
                </Tooltip>
                <Tooltip
                    openDelay={500}
                    label='Toggle search help'
                >
                    <IconButton
                        variant='ghost'
                        size='xs'
                        onClick={() => {
                            toggleHelp()
                            closeQueryDebug()
                        }}
                        aria-label='Toggle search help'
                        icon={<FiInfo fontSize='0.9rem'/>}
                    />
                </Tooltip>
            </InputRightElement>
        </InputGroup>
    )

    return (
        <div
            style={{
                padding: '0 var(--chakra-space-6)',
                display: 'flex',
                justifyContent: 'left',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                marginBottom: 10,
                fontFamily: 'var(--code-font-family)'
            }}
            onKeyPress={handleKeyPress}
        >
            <HStack width='100%'>
                <IconButton
                    onClick={search}
                    aria-label='Search'
                    icon={
                        searchInProgress
                            ? <Spinner size='sm'/>
                        : ((!query && !filteredSessions) || isSearchChanged)
                            ? <FiSearch fontSize='1rem'/> 
                            : <FiRefreshCw  style={{ transform: 'rotate(45deg)' }}/>
                    }
                />
                
                {showSessionIdSelector ? sessionIdSelector() : fixFieldsAutocompleteInput("Search Query")}
            </HStack>
            {showSessionIdSelector && fixFieldsAutocompleteInput("Filter Query")}
            <Collapse in={isQueryDebugOpen} animateOpacity style={{ width: '100%'}}>
                <TableContainer width='100%'>
                    <Table style={{ tableLayout: 'fixed' }}>
                        <Tbody>
                            <Tr>
                                <TdName>Session Query</TdName>
                                <TdValue><CodeBlock whiteSpace='normal'>{searchRequest.primaryQuery}</CodeBlock></TdValue>
                            </Tr>
                            <Tr>
                                <TdName>Filter Query</TdName>
                                <TdValue><CodeBlock whiteSpace='normal'>{searchRequest.secondaryQuery ? searchRequest.secondaryQuery : '<undefined>'}</CodeBlock></TdValue>
                            </Tr>
                            <Tr>
                                <TdName>DB Query</TdName>
                                <TdValue>
                                    <CollapsableCodeBlock>
                                        {searchInProgress ? <Spinner size='sm'/>
                                            : dbQuery ? JSON.stringify(JSON.parse(dbQuery), null, 2)
                                            : '-'}
                                    </CollapsableCodeBlock>
                                </TdValue>
                            </Tr>
                            <Tr>
                                <TdName>Search Results</TdName>
                                <TdValue>
                                    <CollapsableCodeBlock>
                                        {isQueryDebugOpen && (searchInProgress ? <Spinner size='sm'/>
                                            : searchResponse ? JSON.stringify(searchResponse, null, 2)
                                            : '-')}
                                    </CollapsableCodeBlock>
                                </TdValue>
                            </Tr>
                        </Tbody>
                    </Table>
                </TableContainer>
            </Collapse>
            <Collapse in={isHelpOpen} animateOpacity>
                <Flex
                    flexDirection='column'
                    alignItems='flex-start'
                    marginTop={5}
                >
                    <Text>Use <Kbd backgroundColor='keyboard-key'>Enter</Kbd> hot key to search.</Text>
                    <Text>Use <Kbd backgroundColor='keyboard-key'>Shift</Kbd> + <Kbd backgroundColor='keyboard-key'>Enter</Kbd> hot key to add a new line.</Text>
                    <Text>Start typing <Code>{'@<field name>'}</Code> to see the list of fields you can search for.</Text>
                    <Text>Free text search is also supported, for example <Code>ROUTER</Code> will search for all FIX messages that contain "ROUTER".</Text>
                    <Text>This is a simple search query example: <Code>@SenderCompID == "ROUTER"</Code>.</Text>
                    <Text>To concatenate queries use 'and', for example: <Code>@SenderCompID == "ROUTER" and @MsgType != "0"</Code>.</Text>
                    <Text>
                        For more information on search queries see <Link
                            isExternal
                            style={{
                                color: 'var(--chakra-colors-blue-500)',
                                textDecoration: 'underline'
                            }}
                            href="https://docs.chronicle.software/chronicle-fix/chronicle-FIX/monitoring/fix-ui.html#_search_queries">
                            here
                        </Link>
                        .
                    </Text>
                </Flex>
            </Collapse>
        </div>
    )
})
